import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { MonestiqAlgorithmPayload, MonestiqAtmRow, MonestiqAtmsPayload } from '../types/biApp';
import { api, mutate, uploadFile } from '../shared/api';
import { formatNumber, formatDate, formatDateTime } from '../shared/formatting';
import { QuickSearchInput } from '../shared/quickSearch';
import { API_BASE } from '../shared/constants';
import { MonestiqAtmDrawing, MonestiqTribalBand } from './monestiqDecor';
import { hasModuleGrant } from '../shared/biModuleKeys';
import { DateInput } from '../shared/DateInput';

type CalcTab = 'algorithm' | 'manual';

type FormState = {
    company_id: number;
    name: string;
    address: string;
    /** Jedno polje "lat, lng" ili prazno pa koristi samo adresu. */
    coordinates: string;
    docdoc_contract_url: string;
    description: string;
    calc_tab: CalcTab;
    algorithm_mode: 'dcc' | 'af' | 'combined';
    algorithm_scenario_index: number;
    algorithm_dcc_share_index: number;
    calculation_manual: string;
    installed_at: string;
    last_service_at: string;
    next_service_at: string;
    lease_contract_expires_at: string;
};

function sliceDate(raw: string | null | undefined): string {
    if (!raw) {
        return '';
    }
    return raw.length >= 10 ? raw.slice(0, 10) : raw;
}

function emptyForm(defaultCompanyId: number): FormState {
    return {
        company_id: defaultCompanyId,
        name: '',
        address: '',
        coordinates: '',
        docdoc_contract_url: '',
        description: '',
        calc_tab: 'algorithm',
        algorithm_mode: 'dcc',
        algorithm_scenario_index: 0,
        algorithm_dcc_share_index: 0,
        calculation_manual: '',
        installed_at: '',
        last_service_at: '',
        next_service_at: '',
        lease_contract_expires_at: '',
    };
}

function parseCoordinatesToLatLng(raw: string): { latitude: number | null; longitude: number | null } {
    const c = raw.trim();
    if (c === '') {
        return { latitude: null, longitude: null };
    }
    const parts = c.split(/[\s,;]+/).filter((p) => p.length > 0);
    if (parts.length < 2) {
        return { latitude: null, longitude: null };
    }
    const lat = Number(parts[0].replace(',', '.'));
    const lng = Number(parts[1].replace(',', '.'));
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        return { latitude: null, longitude: null };
    }
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        return { latitude: null, longitude: null };
    }

    return { latitude: lat, longitude: lng };
}

function coordinatesFromRow(row: MonestiqAtmRow): string {
    const la = row.latitude?.trim();
    const ln = row.longitude?.trim();
    if (la && ln) {
        return `${la}, ${ln}`;
    }

    return '';
}

function parseOptionalNumber(raw: string): number | null {
    const t = raw.trim().replace(',', '.');
    if (t === '') {
        return null;
    }
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
}

function previewCalculation(
    alg: MonestiqAlgorithmPayload,
    mode: 'dcc' | 'af' | 'combined',
    scenarioIndex: number,
    dccShareIndex: number,
): number | null {
    const s = alg.scenarios[scenarioIndex];
    if (!s) {
        return null;
    }
    const intl = alg.globals.intl_tx_count;
    const dccPerTx = alg.globals.dcc_revenue_per_tx;
    const afAmount = s.af_amount;
    const optIn = s.opt_in;
    if (mode === 'af') {
        return Math.round(intl * optIn * afAmount * 1e6) / 1e6;
    }
    const sharePct = alg.dcc_share_percentages[dccShareIndex];
    if (sharePct === undefined) {
        return null;
    }
    const dccPart = (sharePct / 100) * intl * dccPerTx * optIn;
    if (mode === 'dcc') {
        return Math.round(dccPart * 1e6) / 1e6;
    }
    const afPart = intl * optIn * afAmount;
    return Math.round((dccPart + afPart) * 1e6) / 1e6;
}

function extractAlgorithmFromRow(row: MonestiqAtmRow): {
    mode: 'dcc' | 'af' | 'combined';
    scenario_index: number;
    dcc_share_index: number | null;
} | null {
    const m = row.meta;
    if (!m || typeof m !== 'object') {
        return null;
    }
    const raw = (m as Record<string, unknown>).algorithm;
    if (!raw || typeof raw !== 'object') {
        return null;
    }
    const a = raw as Record<string, unknown>;
    const mode = a.mode;
    if (mode !== 'dcc' && mode !== 'af' && mode !== 'combined') {
        return null;
    }
    const scenarioIndex = typeof a.scenario_index === 'number' ? a.scenario_index : Number(a.scenario_index);
    if (!Number.isFinite(scenarioIndex)) {
        return null;
    }
    let dccShare: number | null = null;
    if (a.dcc_share_index !== null && a.dcc_share_index !== undefined) {
        const n = typeof a.dcc_share_index === 'number' ? a.dcc_share_index : Number(a.dcc_share_index);
        dccShare = Number.isFinite(n) ? n : null;
    }

    return { mode, scenario_index: scenarioIndex, dcc_share_index: dccShare };
}

function rowToForm(row: MonestiqAtmRow): FormState {
    const alg = extractAlgorithmFromRow(row);
    const base: FormState = {
        company_id: row.company_id,
        name: row.name,
        address: row.address ?? '',
        coordinates: coordinatesFromRow(row),
        docdoc_contract_url: row.docdoc_contract_url ?? '',
        description: row.description ?? '',
        calc_tab: alg ? 'algorithm' : 'manual',
        algorithm_mode: alg?.mode ?? 'dcc',
        algorithm_scenario_index: alg?.scenario_index ?? 0,
        algorithm_dcc_share_index: alg?.dcc_share_index ?? 0,
        calculation_manual: row.calculation ?? '',
        installed_at: sliceDate(row.installed_at),
        last_service_at: sliceDate(row.last_service_at),
        next_service_at: sliceDate(row.next_service_at),
        lease_contract_expires_at: sliceDate(row.lease_contract_expires_at),
    };
    return base;
}

function buildPayload(form: FormState): Record<string, unknown> {
    const { latitude: lat, longitude: lng } = parseCoordinatesToLatLng(form.coordinates);

    const base: Record<string, unknown> = {
        company_id: form.company_id,
        name: form.name.trim(),
        address: form.address.trim() || null,
        latitude: lat,
        longitude: lng,
        docdoc_contract_url: form.docdoc_contract_url.trim() || null,
        description: form.description.trim() || null,
        installed_at: form.installed_at.trim() || null,
        last_service_at: form.last_service_at.trim() || null,
        next_service_at: form.next_service_at.trim() || null,
        lease_contract_expires_at: form.lease_contract_expires_at.trim() || null,
    };

    if (form.calc_tab === 'algorithm') {
        base.algorithm_mode = form.algorithm_mode;
        base.algorithm_scenario_index = form.algorithm_scenario_index;
        if (form.algorithm_mode !== 'af') {
            base.algorithm_dcc_share_index = form.algorithm_dcc_share_index;
        }

        return base;
    }

    base.calculation = parseOptionalNumber(form.calculation_manual);

    return base;
}

const PER_PAGE = 25;

function atmShortLocation(row: MonestiqAtmRow): string {
    const la = row.latitude?.trim();
    const ln = row.longitude?.trim();
    if (la && ln) {
        const latN = Number(la);
        const lngN = Number(ln);
        if (Number.isFinite(latN) && Number.isFinite(lngN)) {
            return `${latN.toFixed(5)}, ${lngN.toFixed(5)}`;
        }
    }
    const addr = row.address?.trim();
    if (addr) {
        return addr.length > 52 ? `${addr.slice(0, 49)}…` : addr;
    }

    return row.name;
}

function fmtDueChip(days: number | null | undefined, locale: 'hr' | 'en'): string {
    if (days === null || days === undefined) {
        return '';
    }
    if (days < 0) {
        return locale === 'en' ? `${Math.abs(days)}d overdue` : `${Math.abs(days)} d kasno`;
    }
    if (days === 0) {
        return locale === 'en' ? 'today' : 'danas';
    }

    return locale === 'en' ? `in ${days}d` : `za ${days} d`;
}

/** 20 landing — odabir 20.1 Pregled financija ili 20.2 Bankomati. */
export function MonestiqLanding({ locale, user, onNavigateSub }: { locale: 'hr' | 'en'; user: { allowed_modules?: string[] }; onNavigateSub: (sub: 'monestiq_finance' | 'monestiq_atm') => void }) {
    const tr = (hr: string, en: string) => (locale === 'en' ? en : hr);
    // ACL: prikaži samo podmodule na koje user ima grant (superadmin vidi sve).
    const cards = [
        { key: 'monestiq_finance' as const, code: '20.1', icon: '📊', titleHr: 'Pregled financija', titleEn: 'Financial overview', descHr: 'Prihodi po danu, namira (settlement) i FX marže iz mjesečnih izvještaja.', descEn: 'Daily revenue, settlement and FX margins from monthly reports.' },
        { key: 'monestiq_atm' as const, code: '20.2', icon: '🏧', titleHr: 'Bankomati', titleEn: 'ATMs', descHr: 'Mreža uređaja, lokacije, servis i kalkulacija prihoda (DCC/AF).', descEn: 'Device network, locations, service and revenue calc (DCC/AF).' },
    ].filter((c) => hasModuleGrant(user, c.key));
    const parentGranted = hasModuleGrant(user, 'monestiq');
    // Auto-jump: bez parent granta + točno 1 dostupan podmodul → preskoči landing.
    useEffect(() => {
        if (!parentGranted && cards.length === 1) onNavigateSub(cards[0].key);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (
        <section className="card admin-block">
            <div className="bi-module-hero">
                <div className="bi-module-hero-icon">🌐</div>
                <div className="bi-module-hero-text">
                    <h1>20 · Monestiq</h1>
                    <p className="muted">{tr('Odaberi pregled financija (20.1) ili bankomate (20.2).', 'Choose financial overview (20.1) or ATMs (20.2).')}</p>
                </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16, marginTop: 8 }}>
                {cards.length === 0 ? (
                    <p className="muted" style={{ gridColumn: '1 / -1', fontStyle: 'italic' }}>{tr('Nemaš pristup nijednom Monestiq podmodulu — javi se administratoru.', 'No access to any Monestiq sub-module — contact your administrator.')}</p>
                ) : null}
                {cards.map((c) => (
                    <button key={c.key} type="button" onClick={() => onNavigateSub(c.key)} style={{ textAlign: 'left', cursor: 'pointer', border: '1px solid rgba(var(--ad-soft-rgb),0.35)', borderRadius: 14, padding: '20px 22px', background: 'linear-gradient(180deg,#fffdf6,var(--ad-bg))', boxShadow: '0 4px 18px rgba(107,79,28,0.08)' }}>
                        <div style={{ fontSize: '2rem' }}>{c.icon}</div>
                        <div style={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.74rem', color: 'var(--ad-acc-3)', fontWeight: 700, marginTop: 6 }}>{c.code}</div>
                        <h3 style={{ margin: '4px 0 6px', color: 'var(--ad-ink)' }}>{tr(c.titleHr, c.titleEn)}</h3>
                        <p className="muted" style={{ fontSize: '0.86rem', margin: 0 }}>{tr(c.descHr, c.descEn)}</p>
                    </button>
                ))}
            </div>
        </section>
    );
}

export function MonestiqModule({
    locale,
    token,
    companyIds,
    onBack,
}: {
    locale: 'hr' | 'en';
    token: string | null;
    companyIds: number[];
    onBack?: () => void;
}) {
    const [payload, setPayload] = useState<MonestiqAtmsPayload | null>(null);
    const [loadError, setLoadError] = useState('');
    const [page, setPage] = useState(1);
    const [searchInput, setSearchInput] = useState('');
    const [searchDebounced, setSearchDebounced] = useState('');

    const [editingId, setEditingId] = useState<number | null>(null);
    const [form, setForm] = useState<FormState>(() => emptyForm(0));
    const [saving, setSaving] = useState(false);
    const [localError, setLocalError] = useState('');
    const [attachmentBusy, setAttachmentBusy] = useState(false);

    useEffect(() => {
        const t = window.setTimeout(() => setSearchDebounced(searchInput.trim()), 280);
        return () => window.clearTimeout(t);
    }, [searchInput]);

    useEffect(() => {
        setPage(1);
    }, [searchDebounced, companyIds.join(',')]);

    const load = useCallback(async () => {
        if (!token) {
            setPayload(null);
            return;
        }
        setLoadError('');
        try {
            const params = new URLSearchParams();
            if (companyIds.length > 0) {
                params.set('company_ids', companyIds.join(','));
            }
            params.set('page', String(page));
            params.set('per_page', String(PER_PAGE));
            if (searchDebounced) {
                params.set('search', searchDebounced);
            }
            const data = await api<MonestiqAtmsPayload>(`/monestiq/atms?${params.toString()}`, token);
            setPayload(data);
        } catch (err) {
            setLoadError(err instanceof Error ? err.message : 'API');
            setPayload(null);
        }
    }, [token, companyIds, page, searchDebounced]);

    useEffect(() => {
        void load();
    }, [load]);

    const companies = payload?.companies ?? [];
    const preferredCompanyId = useMemo(() => {
        const list = payload?.companies ?? [];
        const def = payload?.default_company_id;
        if (def && list.some((c) => c.id === def)) {
            return def;
        }

        return list[0]?.id ?? 0;
    }, [payload?.companies, payload?.default_company_id]);
    const listMeta = payload?.meta;
    const algorithm = payload?.algorithm;
    useEffect(() => {
        if (preferredCompanyId <= 0) {
            return;
        }
        setForm((current) => {
            if (editingId != null) {
                return current;
            }
            if (current.company_id > 0 && companies.some((c) => c.id === current.company_id)) {
                return current;
            }

            return { ...current, company_id: preferredCompanyId };
        });
    }, [preferredCompanyId, companies, editingId]);

    const rows = payload?.rows ?? [];
    const editingRow = useMemo(() => rows.find((r) => r.id === editingId), [rows, editingId]);

    const calcPreview = useMemo(() => {
        if (!algorithm || form.calc_tab !== 'algorithm') {
            return null;
        }

        return previewCalculation(
            algorithm,
            form.algorithm_mode,
            form.algorithm_scenario_index,
            form.algorithm_dcc_share_index,
        );
    }, [
        algorithm,
        form.calc_tab,
        form.algorithm_mode,
        form.algorithm_scenario_index,
        form.algorithm_dcc_share_index,
    ]);

    const scenarioChipText = useMemo(() => {
        if (!algorithm) {
            return '';
        }
        const s = algorithm.scenarios[form.algorithm_scenario_index];
        if (!s) {
            return '';
        }
        return locale === 'en' ? `Rows ${s.excel_dcc_row}/${s.excel_af_row}` : `Red ${s.excel_dcc_row}/${s.excel_af_row}`;
    }, [algorithm, form.algorithm_scenario_index, locale]);

    const dccSharePct = useMemo(() => {
        if (!algorithm) {
            return null;
        }
        const p = algorithm.dcc_share_percentages[form.algorithm_dcc_share_index];

        return p === undefined ? null : p;
    }, [algorithm, form.algorithm_dcc_share_index]);

    const scenarioSliderMax = algorithm ? Math.max(0, algorithm.scenarios.length - 1) : 0;
    const dccSliderMax = algorithm ? Math.max(0, algorithm.dcc_shares.length - 1) : 0;

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (!token || saving) {
            return;
        }
        setLocalError('');
        if (form.calc_tab === 'algorithm' && !algorithm) {
            setLocalError(locale === 'en' ? 'Algorithm options not loaded.' : 'Opcije algoritma nisu učitane.');
            return;
        }
        const body = buildPayload(form);
        if (typeof body.name !== 'string' || body.name === '') {
            setLocalError(locale === 'en' ? 'Name is required.' : 'Naziv je obavezan.');
            return;
        }
        setSaving(true);
        try {
            if (editingId != null) {
                await mutate(`/monestiq/atms/${editingId}`, token, 'PATCH', body);
            } else {
                await mutate('/monestiq/atms', token, 'POST', body);
            }
            setEditingId(null);
            setForm(emptyForm(preferredCompanyId));
            await load();
        } catch (err) {
            setLocalError(err instanceof Error ? err.message : 'API error');
        } finally {
            setSaving(false);
        }
    }

    async function handleDelete(id: number) {
        if (!token || saving) {
            return;
        }
        const ok =
            locale === 'en'
                ? window.confirm('Soft-delete this ATM record?')
                : window.confirm('Obrisati zapis ATM-a (soft delete)?');
        if (!ok) {
            return;
        }
        setSaving(true);
        setLocalError('');
        try {
            await mutate(`/monestiq/atms/${id}`, token, 'DELETE');
            if (editingId === id) {
                setEditingId(null);
                setForm(emptyForm(preferredCompanyId));
            }
            await load();
        } catch (err) {
            setLocalError(err instanceof Error ? err.message : 'API error');
        } finally {
            setSaving(false);
        }
    }

    async function handleAttachmentUpload(ev: React.ChangeEvent<HTMLInputElement>) {
        const file = ev.target.files?.[0];
        ev.target.value = '';
        if (!file || !token || editingId == null) {
            return;
        }
        setAttachmentBusy(true);
        setLocalError('');
        try {
            const fd = new FormData();
            fd.append('file', file);
            await uploadFile<{ ok: boolean }>(`/monestiq/atms/${editingId}/attachments`, token, fd);
            await load();
        } catch (err) {
            setLocalError(err instanceof Error ? err.message : 'Upload');
        } finally {
            setAttachmentBusy(false);
        }
    }

    async function handleAttachmentDelete(attachmentId: string) {
        if (!token || editingId == null) {
            return;
        }
        setAttachmentBusy(true);
        setLocalError('');
        try {
            await mutate(`/monestiq/atms/${editingId}/attachments/${encodeURIComponent(attachmentId)}`, token, 'DELETE');
            await load();
        } catch (err) {
            setLocalError(err instanceof Error ? err.message : 'API error');
        } finally {
            setAttachmentBusy(false);
        }
    }

    async function downloadAttachmentFile(atmId: number, attachmentId: string, originalName: string) {
        if (!token) {
            return;
        }
        try {
            const r = await fetch(
                `${API_BASE}/monestiq/atms/${atmId}/attachments/${encodeURIComponent(attachmentId)}/download`,
                { headers: { Authorization: `Bearer ${token}`, Accept: '*/*' } },
            );
            if (!r.ok) {
                throw new Error('dl');
            }
            const blob = await r.blob();
            const u = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = u;
            a.download = originalName || 'privitak';
            a.click();
            URL.revokeObjectURL(u);
        } catch {
            setLocalError(locale === 'en' ? 'Download failed.' : 'Preuzimanje nije uspjelo.');
        }
    }

    function startEdit(row: MonestiqAtmRow) {
        setEditingId(row.id);
        setForm(rowToForm(row));
        setLocalError('');
    }

    function cancelEdit() {
        setEditingId(null);
        setForm(emptyForm(preferredCompanyId));
        setLocalError('');
    }

    const pagination = useMemo(() => {
        if (!listMeta) {
            return null;
        }
        const { total, last_page, page: p } = listMeta;
        const from = total === 0 ? 0 : (p - 1) * PER_PAGE + 1;
        const to = Math.min(total, p * PER_PAGE);
        return { total, last_page, from, to };
    }, [listMeta]);

    const t = {
        title: locale === 'en' ? 'ATM registry' : 'Registar ATM-ova',
        sub:
            locale === 'en'
                ? 'Calculation from „Algoritam_T“ workbook (server-side formulas). Optional manual override.'
                : 'Kalkulacija iz radne knjige „Algoritam_T“ (iste formule kao u Excelu). Ručno samo ako treba iznimka.',
        company: locale === 'en' ? 'Company' : 'Tvrtka',
        name: locale === 'en' ? 'Name' : 'Naziv',
        address: locale === 'en' ? 'Address' : 'Adresa',
        coords: locale === 'en' ? 'Coordinates (lat, lng)' : 'Koordinate (lat, lng)',
        installed: locale === 'en' ? 'Installation' : 'Montaža',
        lastSvc: locale === 'en' ? 'Last service' : 'Zadnji servis',
        nextSvc: locale === 'en' ? 'Next service' : 'Sljedeći servis',
        leaseEnd: locale === 'en' ? 'Lease ends' : 'Istek najma',
        dashSvc: locale === 'en' ? 'Service window' : 'Servis u roku',
        dashLease: locale === 'en' ? 'Lease expiry' : 'Istek ugovora najma',
        calc: locale === 'en' ? 'Calculation' : 'Kalkulacija',
        contract: locale === 'en' ? 'Doc / contract' : 'Doc / ugovor',
        newRecord: locale === 'en' ? 'New ATM' : 'Novi ATM',
        edit: locale === 'en' ? 'Edit' : 'Uredi',
        delete: locale === 'en' ? 'Delete' : 'Obriši',
        save: locale === 'en' ? 'Save' : 'Spremi',
        cancel: locale === 'en' ? 'Cancel' : 'Odustani',
        noRows: locale === 'en' ? 'No rows match filters / search.' : 'Nema redaka za filter / pretragu.',
        formHint:
            locale === 'en'
                ? editingId != null
                    ? 'Editing record — save or cancel.'
                    : 'Create a new record for the selected company.'
                : editingId != null
                  ? 'Uređivanje zapisa — spremi ili odustani.'
                  : 'Novi zapis za odabranu tvrtku.',
        prev: locale === 'en' ? 'Previous' : 'Prethodna',
        next: locale === 'en' ? 'Next' : 'Sljedeća',
        pageOf: (a: number, b: number) =>
            locale === 'en' ? `Page ${a} / ${b}` : `Stranica ${a} / ${b}`,
        showing: (from: number, to: number, total: number) =>
            locale === 'en' ? `Showing ${from}–${to} of ${total}` : `Prikaz ${from}–${to} od ${total}`,
        tabAlgo: locale === 'en' ? 'From workbook' : 'Iz Algoritam knjige',
        tabManual: locale === 'en' ? 'Manual (exception)' : 'Ručno (iznimka)',
        preview: locale === 'en' ? 'Preview' : 'Pregled',
        mode: locale === 'en' ? 'Model' : 'Model',
        scenario: locale === 'en' ? 'Scenario (AF / opt-in)' : 'Scenarij (AF / opt-in)',
        dccShare: locale === 'en' ? 'DCC share column' : 'Stupac DCC share %',
        slidersHint: locale === 'en' ? 'Drag sliders — values snap to the workbook grid.' : 'Povuci klizače — vrijednosti „klikaju“ kao u knjizi.',
    };

    if (!token) {
        return (
            <section className="card">
                {locale === 'en' ? 'Sign in to use Monestiq.' : 'Prijavi se za Monestiq.'}
            </section>
        );
    }

    return (
        <div className="monestiq-atlas">
            <section className="module-stack">
                {onBack ? <button type="button" className="secondary compact" onClick={onBack} style={{ alignSelf: 'flex-start', marginBottom: 8 }}>← {locale === 'en' ? 'Back to Monestiq' : 'Natrag na Monestiq'}</button> : null}
                <section className="card hero-module monestiq-hero-card">
                    <div className="monestiq-hero-band" aria-hidden>
                        <MonestiqTribalBand />
                    </div>
                    <div className="monestiq-hero-layout">
                        <div className="monestiq-hero-copy module-heading">
                            <div>
                                <p className="eyebrow">Monestiq</p>
                                <h2>{t.title}</h2>
                                <p className="muted monestiq-hero-lead">{t.sub}</p>
                            </div>
                        </div>
                        <div className="monestiq-hero-visual" aria-hidden>
                            <MonestiqAtmDrawing />
                        </div>
                    </div>
                </section>

                {import.meta.env.DEV ? (
                    <section className="card monestiq-glass-card" style={{ padding: '12px 16px' }}>
                        <p className="muted" style={{ margin: 0, fontSize: '0.84rem', lineHeight: 1.45 }}>
                            <strong>{locale === 'en' ? 'Local ledger analytics:' : 'Lokalna analitika dnevnika:'}</strong>{' '}
                            {locale === 'en'
                                ? 'Import Monestiq journal XLSX into SQLite and export trial-balance / URA–IRA heuristic CSVs — see repo folder '
                                : 'Uvoz Monestiq dnevnika knjiženja u SQLite i CSV izvještaji (bruto bilanca, heuristika URA/IRA) — vidi mapu '}
                            <code style={{ fontSize: '0.8rem' }}>AD/local_analytics/</code>
                            {locale === 'en' ? ' (README.md).' : ' (README.md).'}
                        </p>
                    </section>
                ) : null}

                {payload?.dashboard ? (
                    <section className="card monestiq-glass-card monestiq-dashboard-panel">
                        <div className="module-heading compact-heading">
                            <h3>{locale === 'en' ? 'Operational dashboard' : 'Operativni pregled'}</h3>
                            <span className="pill muted">
                                {locale === 'en'
                                    ? `Service ≤ ${payload.dashboard.horizons.service_days}d · Lease ≤ ${payload.dashboard.horizons.contract_days}d`
                                    : `Servis ≤ ${payload.dashboard.horizons.service_days} d · Najam ≤ ${payload.dashboard.horizons.contract_days} d`}
                            </span>
                        </div>
                        <div className="monestiq-dash-columns">
                            <div>
                                <h4 className="monestiq-dash-sub">{t.dashSvc}</h4>
                                <div className="monestiq-dash-cards">
                                    {payload.dashboard.service_due.length === 0 ? (
                                        <p className="muted small-note">
                                            {locale === 'en' ? 'Nothing in this window.' : 'Ništa u ovom horizontu.'}
                                        </p>
                                    ) : (
                                        payload.dashboard.service_due.map((r) => (
                                            <article key={`svc-${r.id}`} className="monestiq-dash-card">
                                                <strong>
                                                    {r.company_code} · {r.name}
                                                </strong>
                                                <span className="muted">{r.location_label}</span>
                                                <span className="monestiq-dash-due">
                                                    {r.due_date ? formatDate(r.due_date, locale) : '—'}{' '}
                                                    {fmtDueChip(r.days_until, locale) ? (
                                                        <span className="pill">{fmtDueChip(r.days_until, locale)}</span>
                                                    ) : null}
                                                </span>
                                            </article>
                                        ))
                                    )}
                                </div>
                            </div>
                            <div>
                                <h4 className="monestiq-dash-sub">{t.dashLease}</h4>
                                <div className="monestiq-dash-cards">
                                    {payload.dashboard.contracts_expiring.length === 0 ? (
                                        <p className="muted small-note">
                                            {locale === 'en' ? 'Nothing in this window.' : 'Ništa u ovom horizontu.'}
                                        </p>
                                    ) : (
                                        payload.dashboard.contracts_expiring.map((r) => (
                                            <article key={`lease-${r.id}`} className="monestiq-dash-card monestiq-dash-card--lease">
                                                <strong>
                                                    {r.company_code} · {r.name}
                                                </strong>
                                                <span className="muted">{r.location_label}</span>
                                                <span className="monestiq-dash-due">
                                                    {r.due_date ? formatDate(r.due_date, locale) : '—'}{' '}
                                                    {fmtDueChip(r.days_until, locale) ? (
                                                        <span className="pill">{fmtDueChip(r.days_until, locale)}</span>
                                                    ) : null}
                                                </span>
                                            </article>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>
                    </section>
                ) : null}

                <section className="card monestiq-glass-card">
                    <div className="module-heading compact-heading">
                        <h3>{t.newRecord}</h3>
                        <span className="pill">{t.formHint}</span>
                    </div>
                    {localError ? <p className="error">{localError}</p> : null}
                    {loadError ? <p className="error">{loadError}</p> : null}
                    {!payload ? (
                        <p className="muted">{locale === 'en' ? 'Loading…' : 'Učitavam…'}</p>
                    ) : companies.length === 0 ? (
                        <p className="muted">
                            {locale === 'en'
                                ? 'No companies available for your account.'
                                : 'Nema dostupnih tvrtki za tvoj račun.'}
                        </p>
                    ) : (
                        <form onSubmit={handleSubmit}>
                            <div className="admin-form-grid">
                                <label>
                                    {t.company}
                                    <select
                                        className="monestiq-input"
                                        value={form.company_id}
                                        onChange={(e) => setForm((f) => ({ ...f, company_id: Number(e.target.value) }))}
                                        required
                                    >
                                        {companies.map((c) => (
                                            <option key={c.id} value={c.id}>
                                                {c.code} · {c.name}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                <label>
                                    {t.name}
                                    <input
                                        className="monestiq-input"
                                        value={form.name}
                                        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                                        required
                                        maxLength={255}
                                    />
                                </label>
                                <label style={{ gridColumn: '1 / -1' }}>
                                    {t.address}
                                    <input
                                        className="monestiq-input"
                                        value={form.address}
                                        onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                                    />
                                </label>
                                <label style={{ gridColumn: '1 / -1' }}>
                                    {t.coords}
                                    <input
                                        className="monestiq-input"
                                        value={form.coordinates}
                                        onChange={(e) => setForm((f) => ({ ...f, coordinates: e.target.value }))}
                                        placeholder={locale === 'en' ? 'e.g. 45.815399, 15.966568' : 'npr. 45.815399, 15.966568'}
                                        spellCheck={false}
                                        autoComplete="off"
                                    />
                                </label>
                                <label>
                                    {t.installed}
                                    <DateInput
                                        className="monestiq-input"
                                        value={form.installed_at}
                                        onChange={(iso) => setForm((f) => ({ ...f, installed_at: iso }))}
                                    />
                                </label>
                                <label>
                                    {t.lastSvc}
                                    <DateInput
                                        className="monestiq-input"
                                        value={form.last_service_at}
                                        onChange={(iso) => setForm((f) => ({ ...f, last_service_at: iso }))}
                                    />
                                </label>
                                <label>
                                    {t.nextSvc}
                                    <DateInput
                                        className="monestiq-input"
                                        value={form.next_service_at}
                                        onChange={(iso) => setForm((f) => ({ ...f, next_service_at: iso }))}
                                    />
                                </label>
                                <label>
                                    {t.leaseEnd}
                                    <DateInput
                                        className="monestiq-input"
                                        value={form.lease_contract_expires_at}
                                        onChange={(iso) =>
                                            setForm((f) => ({ ...f, lease_contract_expires_at: iso }))
                                        }
                                    />
                                </label>

                                <div className="monestiq-calc-field">
                                    <div className="monestiq-field-label" id="monestiq-calc-heading">
                                        {t.calc}
                                    </div>
                                    <div
                                        className="monestiq-source-switch"
                                        role="tablist"
                                        aria-labelledby="monestiq-calc-heading"
                                    >
                                        <button
                                            type="button"
                                            role="tab"
                                            aria-selected={form.calc_tab === 'algorithm'}
                                            className={`monestiq-source-switch__btn${form.calc_tab === 'algorithm' ? ' is-active' : ''}`}
                                            onClick={() => setForm((f) => ({ ...f, calc_tab: 'algorithm' }))}
                                        >
                                            {t.tabAlgo}
                                        </button>
                                        <button
                                            type="button"
                                            role="tab"
                                            aria-selected={form.calc_tab === 'manual'}
                                            className={`monestiq-source-switch__btn${form.calc_tab === 'manual' ? ' is-active' : ''}`}
                                            onClick={() => setForm((f) => ({ ...f, calc_tab: 'manual' }))}
                                        >
                                            {t.tabManual}
                                        </button>
                                    </div>

                                    {form.calc_tab === 'algorithm' && algorithm ? (
                                        <div className="monestiq-calc-playground" data-mode={form.algorithm_mode}>
                                            <p className="monestiq-calc-lede muted">{t.slidersHint}</p>
                                            <div className="monestiq-mode-cluster">
                                                <span className="monestiq-mode-cluster__title">{t.mode}</span>
                                                <div className="monestiq-mode-toggle" role="group" aria-label={t.mode}>
                                                    {algorithm.modes.map((m) => (
                                                        <button
                                                            key={m.value}
                                                            type="button"
                                                            className={`monestiq-mode-btn monestiq-mode-btn--${m.value}${
                                                                form.algorithm_mode === m.value ? ' is-pressed' : ''
                                                            }`}
                                                            aria-pressed={form.algorithm_mode === m.value}
                                                            onClick={() =>
                                                                setForm((f) => ({
                                                                    ...f,
                                                                    algorithm_mode: m.value as FormState['algorithm_mode'],
                                                                }))
                                                            }
                                                        >
                                                            {locale === 'en' ? m.label_en : m.label_hr}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>

                                            <div className="monestiq-slider-block">
                                                <div className="monestiq-slider-block__row">
                                                    <span className="monestiq-slider-block__label">{t.scenario}</span>
                                                    <span className="monestiq-slider-block__chip" aria-live="polite">
                                                        {scenarioChipText}
                                                    </span>
                                                </div>
                                                <input
                                                    type="range"
                                                    className="monestiq-range monestiq-range--scenario"
                                                    min={0}
                                                    max={scenarioSliderMax}
                                                    step={1}
                                                    value={form.algorithm_scenario_index}
                                                    onChange={(e) =>
                                                        setForm((f) => ({
                                                            ...f,
                                                            algorithm_scenario_index: Number(e.target.value),
                                                        }))
                                                    }
                                                    aria-valuetext={
                                                        algorithm.scenarios[form.algorithm_scenario_index]
                                                            ? locale === 'en'
                                                              ? algorithm.scenarios[form.algorithm_scenario_index].label_en
                                                              : algorithm.scenarios[form.algorithm_scenario_index].label_hr
                                                            : undefined
                                                    }
                                                />
                                                <p className="monestiq-slider-block__caption muted">
                                                    {algorithm.scenarios[form.algorithm_scenario_index]
                                                        ? locale === 'en'
                                                            ? algorithm.scenarios[form.algorithm_scenario_index].label_en
                                                            : algorithm.scenarios[form.algorithm_scenario_index].label_hr
                                                        : ''}
                                                </p>
                                            </div>

                                            {form.algorithm_mode !== 'af' ? (
                                                <div className="monestiq-slider-block">
                                                    <div className="monestiq-slider-block__row">
                                                        <span className="monestiq-slider-block__label">{t.dccShare}</span>
                                                        <span className="monestiq-slider-block__chip">
                                                            {dccSharePct !== null ? `${dccSharePct}%` : '—'}
                                                        </span>
                                                    </div>
                                                    <input
                                                        type="range"
                                                        className="monestiq-range monestiq-range--share"
                                                        min={0}
                                                        max={dccSliderMax}
                                                        step={1}
                                                        value={form.algorithm_dcc_share_index}
                                                        onChange={(e) =>
                                                            setForm((f) => ({
                                                                ...f,
                                                                algorithm_dcc_share_index: Number(e.target.value),
                                                            }))
                                                        }
                                                        aria-valuenow={form.algorithm_dcc_share_index}
                                                        aria-valuetext={
                                                            dccSharePct !== null ? `${dccSharePct}%` : undefined
                                                        }
                                                    />
                                                </div>
                                            ) : null}

                                            <div className="monestiq-calc-preview-box monestiq-calc-preview-box--neon">
                                                <span className="monestiq-calc-preview-box__eyebrow">{t.preview}</span>
                                                <div className="monestiq-calc-preview-box__value">
                                                    <strong className="monestiq-calc-preview-num">
                                                        {calcPreview !== null ? formatNumber(calcPreview, locale) : '—'}
                                                    </strong>
                                                    <span className="muted monestiq-calc-version">v{algorithm.version}</span>
                                                </div>
                                            </div>
                                        </div>
                                    ) : form.calc_tab === 'algorithm' ? (
                                        <p className="muted">{locale === 'en' ? 'Loading algorithm…' : 'Učitavam algoritam…'}</p>
                                    ) : (
                                        <div className="monestiq-manual-panel">
                                            <span className="monestiq-manual-panel__label">{t.tabManual}</span>
                                            <input
                                                type="number"
                                                className="monestiq-input monestiq-manual-panel__input"
                                                step="any"
                                                value={form.calculation_manual}
                                                onChange={(e) => setForm((f) => ({ ...f, calculation_manual: e.target.value }))}
                                                aria-label={t.calc}
                                            />
                                        </div>
                                    )}
                                </div>

                                <label style={{ gridColumn: '1 / -1' }}>
                                    {t.contract}
                                    <input
                                        className="monestiq-input"
                                        value={form.docdoc_contract_url}
                                        onChange={(e) => setForm((f) => ({ ...f, docdoc_contract_url: e.target.value }))}
                                        placeholder="https://"
                                    />
                                </label>
                                <label style={{ gridColumn: '1 / -1' }}>
                                    {locale === 'en' ? 'Description' : 'Opis'}
                                    <textarea
                                        className="monestiq-input monestiq-textarea"
                                        rows={3}
                                        value={form.description}
                                        onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                                    />
                                </label>
                                {editingId != null ? (
                                    <div style={{ gridColumn: '1 / -1' }} className="monestiq-attachments-block">
                                        <p className="eyebrow" style={{ marginBottom: 8 }}>
                                            {locale === 'en' ? 'Statement attachments' : 'Privitci izvoda'}
                                        </p>
                                        <ul className="muted" style={{ margin: '0 0 8px 1rem', padding: 0 }}>
                                            {(editingRow?.statement_attachments ?? []).map((att) => (
                                                <li key={att.id} style={{ marginBottom: 4 }}>
                                                    <button
                                                        type="button"
                                                        className="link"
                                                        onClick={() =>
                                                            void downloadAttachmentFile(editingId, att.id, att.original_name ?? 'file')
                                                        }
                                                    >
                                                        {att.original_name ?? att.id}
                                                    </button>
                                                    {att.size != null ? (
                                                        <span className="muted"> ({Math.round(att.size / 1024)} KB)</span>
                                                    ) : null}
                                                    <button
                                                        type="button"
                                                        className="secondary compact"
                                                        style={{ marginLeft: 8 }}
                                                        disabled={attachmentBusy}
                                                        onClick={() => void handleAttachmentDelete(att.id)}
                                                    >
                                                        {locale === 'en' ? 'Remove' : 'Ukloni'}
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                        <label className="filter-label">
                                            {locale === 'en' ? 'Add file' : 'Dodaj datoteku'}
                                            <input
                                                type="file"
                                                disabled={attachmentBusy}
                                                onChange={(ev) => void handleAttachmentUpload(ev)}
                                            />
                                        </label>
                                    </div>
                                ) : (
                                    <p className="muted" style={{ gridColumn: '1 / -1' }}>
                                        {locale === 'en'
                                            ? 'Save the record first, then you can attach bank statement files.'
                                            : 'Prvo spremi zapis, zatim možeš dodati privitke izvoda.'}
                                    </p>
                                )}
                            </div>
                            <div className="admin-toolbar" style={{ marginTop: 14 }}>
                                <button type="submit" className="primary" disabled={saving}>
                                    {t.save}
                                </button>
                                {editingId != null ? (
                                    <button type="button" className="secondary" onClick={cancelEdit} disabled={saving}>
                                        {t.cancel}
                                    </button>
                                ) : null}
                            </div>
                        </form>
                    )}
                </section>

                <section className="card monestiq-glass-card">
                    <div className="module-heading compact-heading monestiq-table-toolbar">
                        <h3>{locale === 'en' ? 'Records' : 'Zapisi'}</h3>
                        <QuickSearchInput
                            value={searchInput}
                            onChange={setSearchInput}
                            placeholder={locale === 'en' ? 'Search name, address, company…' : 'Pretraga: naziv, adresa, tvrtka…'}
                        />
                    </div>
                    {payload && pagination ? (
                        <p className="muted monestiq-page-summary">
                            {t.showing(pagination.from, pagination.to, pagination.total)} · {t.pageOf(page, pagination.last_page)}
                        </p>
                    ) : null}
                    {!payload ? null : rows.length === 0 ? (
                        <p className="muted">{t.noRows}</p>
                    ) : (
                        <>
                            <div className="monestiq-atm-card-grid">
                                {rows.map((row) => (
                                    <article key={row.id} className="card monestiq-atm-mini-card">
                                        <div className="monestiq-atm-mini-card__head">
                                            <strong>{row.name}</strong>
                                            <span className="muted">{row.company_code}</span>
                                        </div>
                                        <p className="monestiq-atm-mini-card__loc muted">
                                            {atmShortLocation(row)}
                                        </p>
                                        <ul className="monestiq-atm-mini-dates muted">
                                            {row.installed_at ? (
                                                <li>
                                                    {t.installed}: {formatDate(sliceDate(row.installed_at), locale)}
                                                </li>
                                            ) : null}
                                            {row.last_service_at ? (
                                                <li>
                                                    {t.lastSvc}: {formatDate(sliceDate(row.last_service_at), locale)}
                                                </li>
                                            ) : null}
                                            {row.next_service_at ? (
                                                <li>
                                                    {t.nextSvc}: {formatDate(sliceDate(row.next_service_at), locale)}
                                                </li>
                                            ) : null}
                                            {row.lease_contract_expires_at ? (
                                                <li>
                                                    {t.leaseEnd}:{' '}
                                                    {formatDate(sliceDate(row.lease_contract_expires_at), locale)}
                                                </li>
                                            ) : null}
                                        </ul>
                                        {(row.created_at || row.updated_at) ? (
                                            <p className="muted small-note" style={{ marginTop: 6 }}>
                                                {row.created_at ? (
                                                    <span>
                                                        {locale === 'en' ? 'Created' : 'Kreirano'}: {formatDateTime(row.created_at, locale)}
                                                    </span>
                                                ) : null}
                                                {row.created_at && row.updated_at ? ' · ' : null}
                                                {row.updated_at ? (
                                                    <span>
                                                        {locale === 'en' ? 'Updated' : 'Ažurirano'}: {formatDateTime(row.updated_at, locale)}
                                                    </span>
                                                ) : null}
                                            </p>
                                        ) : null}
                                        <div className="monestiq-atm-mini-calc muted">
                                            {t.calc}:{' '}
                                            {row.calculation != null && row.calculation !== ''
                                                ? formatNumber(Number(row.calculation), locale)
                                                : '—'}
                                        </div>
                                        <p className="muted small-note">
                                            {locale === 'en' ? 'Attachments' : 'Privitci'}:{' '}
                                            {Array.isArray(row.statement_attachments) ? row.statement_attachments.length : 0}
                                        </p>
                                        <div className="admin-toolbar monestiq-atm-mini-actions">
                                            <button type="button" className="secondary compact" onClick={() => startEdit(row)}>
                                                {t.edit}
                                            </button>
                                            <button
                                                type="button"
                                                className="secondary compact"
                                                onClick={() => void handleDelete(row.id)}
                                                disabled={saving}
                                            >
                                                {t.delete}
                                            </button>
                                            {row.docdoc_contract_url ? (
                                                <a
                                                    className="compact secondary"
                                                    href={row.docdoc_contract_url}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    {locale === 'en' ? 'Doc' : 'Doc'}
                                                </a>
                                            ) : null}
                                        </div>
                                    </article>
                                ))}
                            </div>
                            {pagination && pagination.last_page > 1 ? (
                                <div className="admin-toolbar monestiq-pagination">
                                    <button
                                        type="button"
                                        className="secondary compact"
                                        disabled={page <= 1}
                                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    >
                                        {t.prev}
                                    </button>
                                    <button
                                        type="button"
                                        className="secondary compact"
                                        disabled={page >= pagination.last_page}
                                        onClick={() => setPage((p) => Math.min(pagination.last_page, p + 1))}
                                    >
                                        {t.next}
                                    </button>
                                </div>
                            ) : null}
                        </>
                    )}
                </section>
            </section>
        </div>
    );
}
